// Libs
import classNames from 'classnames/bind';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Box, Button, Grid2, Stack } from '@mui/material';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
// Components, Layouts, Pages
import {
  AdditionalContactClientForm,
  AddressInfoClientForm,
  BusinessSettingsClientForm,
  ContactInfoClientForm,
  PersonalInfoClientForm,
} from '~/components/module/client';
import { BaseToolbar, Loading, UnderDevelopment } from '~/components/common';
// Others
import { IBaseResponse } from '~/utils/interface/base';
import { useAppDispatch } from '~/utils/redux/hooks';
import { TypeCrmEnum, TypeDetailClientEnum } from '~/utils/enum/common';
import { IBodyUpdate } from '~/utils/interface/common';
import { IClientOverview, IFormClient, IParamDetailClient } from '~/utils/interface/client';
import { createClient, getDetailClient, updateClient } from '~/thunks/client/clientThunk';
import { BACK_TO_PREVIOUS_PAGE, defaultContact, EMPTY_STRING } from '~/utils/constants/common';
import {
  createFormData,
  formatPhoneNumber,
  getChangedDataForm,
  isEmptyObject,
  removeEmptyObjects,
  removeNonDigits,
} from '~/utils/helper/common';
import { defaultValuesClientForm, schema } from './helper';
// Styles, images, icons
import styles from './ClientForm.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const ClientForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: clientId } = useParams();
  const [searchParams] = useSearchParams();
  const typeList = searchParams.get('type');

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    reset,
    trigger,
    getValues,
    setError,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<IFormClient>({
    resolver: yupResolver(schema(t)),
    defaultValues: {
      ...defaultValuesClientForm,
      clientContacts: clientId ? [] : [defaultContact],
    },
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [initialData, setInitialData] = useState<IFormClient>({
    ...defaultValuesClientForm,
    clientContacts: clientId ? [] : [defaultContact],
  });

  const [deleteAvatar, setDeleteAvatar] = useState<boolean>(false);
  const [removeContactIds, setRemoveContactIds] = useState<string[]>([]);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!clientId) return;

    const params: IParamDetailClient = {
      clientId: clientId,
      type: TypeDetailClientEnum.OVERVIEW,
    };

    handleGetDetailClient(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailClient = (params: IParamDetailClient) => {
    setIsLoading(true);

    dispatch(getDetailClient(params))
      .unwrap()
      .then((res) => {
        const { data } = res as IBaseResponse<IClientOverview>;

        if (!data) return;

        const { id, avatarImage, additionalContact, ...remainingData } = data;

        const body: IFormClient = {
          ...remainingData,
          firstName: remainingData?.firstName ?? EMPTY_STRING,
          lastName: remainingData?.lastName ?? EMPTY_STRING,
          entityName: remainingData?.entityName ?? EMPTY_STRING,
          note: remainingData?.note ?? EMPTY_STRING,
          type: remainingData?.type ?? EMPTY_STRING,
          title: remainingData?.title ?? EMPTY_STRING,
          companyName: remainingData?.companyName ?? EMPTY_STRING,
          country: remainingData?.address?.country ?? EMPTY_STRING,
          address: remainingData?.address?.address ?? EMPTY_STRING,
          unitDetail: remainingData?.address?.unitDetail ?? EMPTY_STRING,
          city: remainingData?.address?.city ?? EMPTY_STRING,
          state: remainingData?.address?.state ?? EMPTY_STRING,
          postalCode: remainingData?.address?.postalCode ?? EMPTY_STRING,
          lat: String(remainingData?.address?.lat) ?? EMPTY_STRING,
          lng: String(remainingData?.address?.lng) ?? EMPTY_STRING,
          assignedAccountId: remainingData?.assignedUser?.id ?? EMPTY_STRING,
          paymentTerms: remainingData?.paymentTerms ?? EMPTY_STRING,
          salesAccountId: remainingData?.salesUser?.id ?? EMPTY_STRING,
          businessType: remainingData?.businessType ?? EMPTY_STRING,
          // additionalContact: {
          //   firstName: remainingData?.additionalContact?.firstName ?? EMPTY_STRING,
          //   lastName: remainingData?.additionalContact?.lastName ?? EMPTY_STRING,
          //   email: remainingData?.additionalContact?.email ?? EMPTY_STRING,
          //   phoneNumber: remainingData?.additionalContact?.phoneNumber ?? EMPTY_STRING,
          //   contactType: remainingData?.additionalContact?.contactType ?? EMPTY_STRING,
          //   countryCode: remainingData?.additionalContact?.countryCode ?? EMPTY_STRING,
          // },
          clientContacts: remainingData?.clientContacts?.map((item) => ({
            ...item,
            phoneNumber: formatPhoneNumber(item?.phoneNumber),
            email: item?.email || EMPTY_STRING,
          })),
          additionalContact: additionalContact?.map((item) => ({
            ...item,
            idDetail: item?.id,
            phoneNumber: formatPhoneNumber(item?.phoneNumber),
            email: item?.email || EMPTY_STRING,
          })),
          avatarImage: avatarImage ? { url: avatarImage } : {},
        };

        setInitialData(body);
        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFormClient = (data: IFormClient) => {
    if (clientId) {
      handleEditClient(data);
    } else {
      handleAddClient(data);
    }

    setIsLoading(true);
  };

  const handleAddClient = async (data: IFormClient) => {
    const { isDeleteAvatar, ...resData } = data;

    const newData: IFormClient = {
      ...resData,
      type: typeList ?? EMPTY_STRING,
      // additionalContact: {
      //   ...data?.additionalContact,
      //   email: data?.additionalContact?.email || EMPTY_STRING,
      //   phoneNumber: data?.additionalContact?.phoneNumber && removeNonDigits(data?.additionalContact?.phoneNumber),
      // },
      additionalContact: data.additionalContact?.map(({ id, ...item }) => ({
        ...item,
        phoneNumber: removeNonDigits(item.phoneNumber),
      })),

      clientContacts: data.clientContacts?.map(({ id, ...item }) => ({
        ...item,
        phoneNumber: removeNonDigits(item.phoneNumber),
      })),
    };

    const formData = await createFormData(removeEmptyObjects(newData), ['avatarImage']);

    dispatch(createClient(formData))
      .unwrap()
      .then((_res) => {
        handleCancel();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditClient = async (data: IFormClient) => {
    const dataChange = getChangedDataForm(dirtyFields, data) as IFormClient;

    const formatData: IFormClient = {
      ...dataChange,
      ...(typeList === TypeCrmEnum.CLIENT && {
        avatarImage: dataChange.avatarImage,
        isDeleteAvatar: isEmptyObject(dataChange?.avatarImage) ? deleteAvatar : false,
      }),
      ...(removeContactIds.length && { deletedAdditionalContactIds: removeContactIds }),
      // additionalContact: {
      //   ...dataChange?.additionalContact,
      //   email: dataChange?.additionalContact?.email || EMPTY_STRING,
      //   phoneNumber:
      //     dataChange?.additionalContact?.phoneNumber && removeNonDigits(dataChange?.additionalContact?.phoneNumber),
      // },
      additionalContact:
        data?.additionalContact &&
        data?.additionalContact?.map(({ idDetail, ...item }) => ({
          ...item,
          phoneNumber: item.phoneNumber && removeNonDigits(item.phoneNumber),
          id: idDetail || undefined,
          contactType: item?.contactType || EMPTY_STRING,
        })),
      clientContacts:
        data?.clientContacts &&
        data?.clientContacts?.map(({ id, ...item }) => ({
          ...item,
          phoneNumber: item.phoneNumber && removeNonDigits(item.phoneNumber),
        })),
    };

    const formData = await createFormData(formatData, ['avatarImage']);

    const params: IBodyUpdate<FormData> = {
      id: clientId || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateClient(params))
      .unwrap()
      .then(() => {
        handleCancel();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleCancel = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset();
  };

  const handleRemoveContact = (contactId: string) => {
    setRemoveContactIds((prev) => [...prev, contactId]);
  };
  //#endregion Handle Function

  return (
    <Box id='clientFormPage' className={cx('container')}>
      <BaseToolbar
        title={t(
          clientId
            ? typeList === TypeCrmEnum.CLIENT
              ? 'client_form_title_edit'
              : 'lead_form_title_edit'
            : typeList === TypeCrmEnum.CLIENT
            ? 'client_form_title_add'
            : 'lead_form_title_add'
        )}
        isBack
      >
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCancel}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' onClick={handleSubmit(handleFormClient)} disabled={clientId ? !isDirty : false}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </BaseToolbar>

      <FormProvider
        {...({
          control,
          watch,
          reset,
          trigger,
          setValue,
          getValues,
          setError,
          formState: { errors, isDirty, dirtyFields },
        } as UseFormReturn<IFormClient>)}
      >
        <Grid2 className={cx('body')} container spacing={10}>
          <Grid2 size={6}>
            <Stack gap={4}>
              <PersonalInfoClientForm
                typeList={typeList || EMPTY_STRING}
                onDeleteImage={(value) => setDeleteAvatar(value)}
              />

              <ContactInfoClientForm />
            </Stack>
          </Grid2>

          <Grid2 size={6}>
            <Stack gap={4}>
              <BusinessSettingsClientForm typeList={typeList || EMPTY_STRING} />
            </Stack>
          </Grid2>

          <Grid2 size={12}>
            <AddressInfoClientForm />
          </Grid2>

          <Grid2 size={12}>
            <AdditionalContactClientForm />
          </Grid2>
        </Grid2>
      </FormProvider>

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default ClientForm;
